import { useCallback, useRef } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';

import { useDisclosure } from '@chakra-ui/react';

import { useLoadingStore } from '@enterprise/ui';

import type { BillingForm } from '@components/billing/types/billing-form.type';
import type { BillingProps } from '@components/billing/types/billing.props';

import { useTriggerBilling } from '@components/billing/hooks/trigger-billing.hook';

export const useBillingConfiguration = ({ onClose }: BillingProps) => {
  const { onClose: onBillingClose, onOpen } = useDisclosure({ isOpen: true });
  const methods = useForm<BillingForm>({
    defaultValues: {
      endDate: undefined,
      startDate: undefined,
    },
    mode: 'onChange',
  });
  const { setIsLoading } = useLoadingStore();
  const { mutateAsync: triggerBilling } = useTriggerBilling();

  const { handleSubmit, reset } = methods;
  const formRef = useRef<HTMLFormElement>(null);

  const handleCancel = useCallback(() => {
    onClose();
    reset({
      endDate: undefined,
      startDate: undefined,
    });
  }, [reset, onClose]);

  const handleOnClose = useCallback(() => {
    onBillingClose();
    onClose();
  }, [onBillingClose, onClose]);

  const onSubmit: SubmitHandler<BillingForm> = (formData) => {
    const { endDate, startDate } = formData;

    setIsLoading(true);

    void triggerBilling({
      endDate,
      startDate,
    }).finally(() => {
      setIsLoading(false);
    });

    onClose();
  };

  return {
    formRef,
    handleCancel,
    handleOnClose,
    handleSubmit,
    methods,
    onBillingClose,
    onOpen,
    onSubmit,
  };
};
